import React from "react";
import { Phone, Mail, MapPin, MessageCircle, ArrowRight } from "lucide-react";
import { useI18n } from "../context/I18nContext";

export function Contact({ settings = {} }) {
  const { t, lang } = useI18n();

  // Build WhatsApp URL from site settings
  const phone = settings?.whatsapp_phone ? settings.whatsapp_phone.replace(/\D/g, "") : "";
  const waText = lang === "tr"
    ? (settings?.whatsapp_message_tr || "Merhaba YGS, web sitenizden ulaşıyorum. Bir teklif almak istiyorum.")
    : (settings?.whatsapp_message_en || "Hi YGS, I'm reaching out from your website. I'd like to request a quote.");
  const waUrl = phone
    ? `https://wa.me/${phone}?text=${encodeURIComponent(waText)}`
    : settings.whatsapp || "";

  const ctaLabel = lang === "tr" ? "WhatsApp ile Teklif Al" : "Get a Quote on WhatsApp";
  const callLabel = lang === "tr" ? "Hemen Ara" : "Call Now";

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="py-20 lg:py-32 bg-white"
    >
      <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12">

          {/* Left: info + map */}
          <div className="lg:col-span-5">
            <div data-reveal className="text-[11px] uppercase tracking-[0.22em] font-bold text-[#0d1b2a]/60">
              {t.contact.eyebrow}
            </div>
            <h2
              data-reveal
              className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight text-[#0d1b2a] leading-[1.05]"
              style={{ fontFamily: "Chivo, Inter, sans-serif" }}
              data-testid="contact-heading"
            >
              {t.contact.heading}
            </h2>
            <p data-reveal className="mt-5 text-base text-[#0d1b2a]/70 leading-relaxed">
              {t.contact.sub}
            </p>

            <div className="mt-8 space-y-4">
              {settings.phone && (
                <a
                  href={`tel:${settings.phone}`}
                  data-testid="contact-info-phone"
                  className="flex items-start gap-4 p-4 bg-[#f6f6f6] border border-[#0d1b2a]/10 rounded-lg hover:border-[#ffb703] transition-colors"
                >
                  <div className="w-10 h-10 rounded-md bg-[#0d1b2a] text-[#ffb703] flex items-center justify-center shrink-0">
                    <Phone size={16} />
                  </div>
                  <div>
                    <div className="text-[11px] uppercase tracking-wider font-bold text-[#0d1b2a]/60">
                      {t.contact.callUs}
                    </div>
                    <div className="text-sm font-bold text-[#0d1b2a] mt-0.5">
                      {settings.phone || "+90 212 555 0 144"}
                    </div>
                    {settings.phone2 && (
                      <div className="text-sm font-bold text-[#0d1b2a] mt-0.5">
                        {settings.phone2}
                      </div>
                    )}
                  </div>
                </a>
              )}
              {(settings.email || "info@ygs.com.tr") && (
                <a
                  href={`mailto:${settings.email || "info@ygs.com.tr"}`}
                  data-testid="contact-info-email"
                  className="flex items-start gap-4 p-4 bg-[#f6f6f6] border border-[#0d1b2a]/10 rounded-lg hover:border-[#ffb703] transition-colors"
                >
                  <div className="w-10 h-10 rounded-md bg-[#0d1b2a] text-[#ffb703] flex items-center justify-center shrink-0">
                    <Mail size={16} />
                  </div>
                  <div>
                    <div className="text-[11px] uppercase tracking-wider font-bold text-[#0d1b2a]/60">
                      {t.contact.mailUs}
                    </div>
                    <div className="text-sm font-bold text-[#0d1b2a] mt-0.5 break-all">
                      {settings.email || "info@ygs.com.tr"}
                    </div>
                  </div>
                </a>
              )}
              {(settings.address || "İkitelli OSB, Atatürk Bulvarı No: 124, Başakşehir / İstanbul") && (
                <div
                  data-testid="contact-info-address"
                  className="flex items-start gap-4 p-4 bg-[#f6f6f6] border border-[#0d1b2a]/10 rounded-lg"
                >
                  <div className="w-10 h-10 rounded-md bg-[#0d1b2a] text-[#ffb703] flex items-center justify-center shrink-0">
                    <MapPin size={16} />
                  </div>
                  <div>
                    <div className="text-[11px] uppercase tracking-wider font-bold text-[#0d1b2a]/60">
                      {t.contact.address}
                    </div>
                    <div className="text-sm font-semibold text-[#0d1b2a] mt-0.5">
                      {settings.address || "İkitelli OSB, Atatürk Bulvarı No: 124, Başakşehir / İstanbul"}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {settings.map_embed_url && (
              <div className="mt-6 aspect-[5/3] rounded-xl overflow-hidden border border-[#0d1b2a]/10 bg-[#f6f6f6]">
                <iframe
                  title="YGS Konum"
                  src={settings.map_embed_url}
                  data-testid="contact-map"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </div>
            )}
          </div>

          {/* Right: WhatsApp CTA panel */}
          <div
            data-reveal
            data-testid="contact-whatsapp-panel"
            className="lg:col-span-7 bg-[#0d1b2a] text-white rounded-2xl p-7 lg:p-10 relative overflow-hidden flex flex-col justify-between"
          >
            <div className="absolute inset-0 bg-grid opacity-30 pointer-events-none" />
            <div className="relative">
              {/* Trust badge */}
              <div
                data-testid="contact-trust-badge"
                className="mb-8 flex items-center gap-3 bg-[#ffb703]/10 border border-[#ffb703]/30 rounded-lg px-4 py-3"
              >
                <span className="relative flex h-2.5 w-2.5 shrink-0">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#ffb703] opacity-60" />
                  <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-[#ffb703]" />
                </span>
                <span className="text-xs sm:text-sm font-semibold text-white/90">
                  {settings.trust_badge_text ||
                    (lang === "tr"
                      ? "Genelde 24 saat içinde dönüş yapılır · 7/24 servis garantisi"
                      : "Usually responds within 24 hours · 24/7 service guarantee")}
                </span>
              </div>

              <h3
                className="text-2xl sm:text-3xl lg:text-4xl font-black leading-tight"
                style={{ fontFamily: "Chivo, Inter, sans-serif" }}
              >
                {lang === "tr" ? (
                  <>Hızlı teklif için<br /><span className="text-[#ffb703]">WhatsApp'tan yazın</span></>
                ) : (
                  <>Get a fast quote<br /><span className="text-[#ffb703]">via WhatsApp</span></>
                )}
              </h3>
              <p className="mt-4 text-white/70 text-base leading-relaxed max-w-md">
                {lang === "tr"
                  ? "Ürün, ölçü veya fiyat hakkında soru sormak için doğrudan WhatsApp'tan ulaşın. Teknik ekibimiz en kısa sürede yanıt verir."
                  : "Reach us directly on WhatsApp for product, sizing or pricing questions. Our technical team will respond as soon as possible."}
              </p>

              {/* Feature list */}
              <ul className="mt-8 space-y-3">
                {(lang === "tr"
                  ? ["Ücretsiz keşif randevusu", "Teknik danışmanlık", "Fiyat teklifi", "Servis talebi"]
                  : ["Free site visit booking", "Technical consultation", "Price quotation", "Service request"]
                ).map((item, i) => (
                  <li key={i} className="flex items-center gap-3 text-sm text-white/80">
                    <span className="w-5 h-5 rounded-full bg-[#ffb703]/20 border border-[#ffb703]/40 flex items-center justify-center shrink-0">
                      <span className="w-1.5 h-1.5 rounded-full bg-[#ffb703]" />
                    </span>
                    {item}
                  </li>
                ))}
              </ul>

              {/* CTA buttons */}
              <div className="mt-10 flex flex-col sm:flex-row gap-4">
                <a
                  href={waUrl}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="contact-whatsapp-cta"
                  className="inline-flex items-center justify-center gap-3 bg-[#25D366] hover:bg-[#1eb057] text-white font-bold px-6 py-4 rounded-xl transition-all hover:scale-[1.02] text-base shadow-lg shadow-[#25D366]/20"
                >
                  <MessageCircle size={20} />
                  {ctaLabel}
                </a>
                <a
                  href={`tel:${settings.phone}`}
                  data-testid="contact-call-cta"
                  className="inline-flex items-center justify-center gap-3 bg-white/10 hover:bg-white/15 border border-white/20 text-white font-bold px-6 py-4 rounded-xl transition-all hover:scale-[1.02] text-base"
                >
                  <Phone size={20} />
                  {callLabel}
                  <ArrowRight size={16} />
                </a>
              </div>

              {/* Bottom note */}
              <p className="mt-6 text-xs text-white/40">
                {lang === "tr"
                  ? "WhatsApp'a tıkladığınızda hazır mesaj otomatik doldurulur."
                  : "Clicking WhatsApp pre-fills a ready message for you."}
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
